/**
 * Lightbox module (singleton)
 *
 * Modal de vídeo: portada + botón play encima del `<video>`; al pulsar se
 * ocultan y se muestra el reproductor con controles (mismo patrón que
 * `video-fullwidth`). Triggers: `data-hg-lightbox-video` (+ poster / type opcionales).
 */

class Lightbox {
  private overlay: HTMLElement | null = null
  private video: HTMLVideoElement | null = null
  private playGate: HTMLButtonElement | null = null
  private closeEls: HTMLElement[] = []
  private triggers: HTMLElement[] = []
  private lastFocus: HTMLElement | null = null
  private opened = false
  private isLeaving = false
  private playGateBound = false

  init(container: Document | HTMLElement = document) {
    const triggers = Array.from(container.querySelectorAll<HTMLElement>('[data-hg-lightbox-video]'))
    if (triggers.length === 0) {
      this.removeBodyOverlays()
      return
    }
    if (!this.ensureOverlay(container)) return

    triggers.forEach((el) => {
      el.addEventListener('click', this.onTriggerClick)
      this.triggers.push(el)
    })
  }

  update(container: Document | HTMLElement = document) {
    this.detachTriggers()
    this.detachOverlay()
    this.init(container)
  }

  cleanup() {
    this.detachTriggers()
    this.detachOverlay()
  }

  private detachPlayGate() {
    if (this.playGateBound && this.playGate) {
      this.playGate.removeEventListener('click', this.onPlayGateClick)
    }
    this.playGateBound = false
    this.playGate = null
  }

  private detachOverlay() {
    this.detachPlayGate()
    this.closeEls.forEach((el) => el.removeEventListener('click', this.onCloseClick))
    this.closeEls = []
    document.removeEventListener('keydown', this.onKeydown)
    document.body.classList.remove('has-lightbox-open')
    this.resetVideo()
    this.overlay?.classList.remove('is-open', 'is-leaving', 'is-playing')
    this.overlay?.setAttribute('aria-hidden', 'true')
    this.overlay = null
    this.video = null
    this.opened = false
    this.isLeaving = false
  }

  private detachTriggers() {
    this.triggers.forEach((t) => t.removeEventListener('click', this.onTriggerClick))
    this.triggers = []
  }

  private ensureOverlay(container: Document | HTMLElement): boolean {
    if (this.overlay?.isConnected) return true

    const overlay = container.querySelector<HTMLElement>('.hg-lightbox')
      || document.querySelector<HTMLElement>('.hg-lightbox')
    if (!overlay) return false

    const video = overlay.querySelector<HTMLVideoElement>('.hg-lightbox__video')
    const playGate = overlay.querySelector<HTMLButtonElement>('.hg-lightbox__play-gate')

    if (!video || !playGate) return false

    this.moveOverlayToBody(overlay)

    this.overlay = overlay
    this.video = video
    this.playGate = playGate

    playGate.addEventListener('click', this.onPlayGateClick)
    this.playGateBound = true

    this.closeEls = Array.from(overlay.querySelectorAll<HTMLElement>('[data-hg-lightbox-close]'))
    this.closeEls.forEach((el) => {
      el.addEventListener('click', this.onCloseClick)
    })
    document.addEventListener('keydown', this.onKeydown)

    return true
  }

  private moveOverlayToBody(overlay: HTMLElement) {
    document.querySelectorAll<HTMLElement>('.hg-lightbox').forEach((el) => {
      if (el !== overlay && el.parentElement === document.body) {
        el.parentElement.removeChild(el)
      }
    })

    if (overlay.parentElement !== document.body) {
      document.body.appendChild(overlay)
    }
  }

  private removeBodyOverlays() {
    document.querySelectorAll<HTMLElement>('.hg-lightbox').forEach((overlay) => {
      if (overlay.parentElement === document.body) {
        overlay.querySelector<HTMLVideoElement>('.hg-lightbox__video')?.pause()
        overlay.parentElement.removeChild(overlay)
      }
    })
  }

  private resetVideo() {
    if (!this.video) return

    this.video.pause()
    this.video.removeAttribute('controls')
    this.video.setAttribute('tabindex', '-1')
    try {
      this.video.currentTime = 0
    } catch (_) {
      // currentTime puede lanzar si el media aún no está listo; ignoramos.
    }
  }

  private onPlayGateClick = (e: Event) => {
    e.preventDefault()
    if (!this.overlay || !this.video || !this.opened || this.isLeaving) return
    if (this.overlay.classList.contains('is-playing')) return

    this.overlay.classList.add('is-playing')
    this.video.removeAttribute('tabindex')
    this.video.controls = true
    void this.video.play()
  }

  private onTriggerClick = (e: Event) => {
    e.preventDefault()

    const trigger = e.currentTarget as HTMLElement
    const src = trigger.dataset.hgLightboxVideo
    const poster = trigger.dataset.hgLightboxPoster
    const type = trigger.dataset.hgLightboxType

    if (!src || !this.video || !this.overlay) return

    this.lastFocus = document.activeElement as HTMLElement | null

    this.overlay.classList.remove('is-playing')

    this.video.innerHTML = ''
    this.video.removeAttribute('controls')
    if (poster) {
      this.video.poster = poster
    } else {
      this.video.removeAttribute('poster')
    }

    const source = document.createElement('source')
    source.src = src
    if (type) source.type = type
    this.video.appendChild(source)
    this.video.load()

    const coverImg = this.overlay.querySelector<HTMLImageElement>('.hg-lightbox__cover')
    const coverWrap = this.overlay.querySelector<HTMLElement>('.hg-lightbox__cover-wrap')
    if (coverImg && coverWrap) {
      coverWrap.classList.remove('hg-lightbox__cover-wrap--empty')
      if (poster) {
        coverImg.src = poster
        coverImg.classList.remove('hg-lightbox__cover--hidden')
      } else {
        coverImg.removeAttribute('src')
        coverImg.classList.add('hg-lightbox__cover--hidden')
        coverWrap.classList.add('hg-lightbox__cover-wrap--empty')
      }
    }

    this.open()
  }

  private open() {
    if (!this.overlay) return
    this.opened = true
    this.overlay.classList.remove('is-leaving')
    this.overlay.setAttribute('aria-hidden', 'false')
    document.body.classList.add('has-lightbox-open')

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!this.overlay) return
        this.overlay.classList.add('is-open')
        this.playGate?.focus()
      })
    })
  }

  private close() {
    if (!this.overlay || !this.video || !this.opened || this.isLeaving) return

    this.isLeaving = true

    this.resetVideo()
    this.overlay.classList.remove('is-playing')

    const backdrop = this.overlay.querySelector('.hg-lightbox__backdrop') as HTMLElement | null

    let finished = false
    const finalize = () => {
      if (finished || !this.overlay) return
      finished = true
      window.clearTimeout(fallbackTimer)
      if (backdrop) {
        backdrop.removeEventListener('transitionend', onBackdropTransitionEnd)
      }

      this.overlay.classList.remove('is-open', 'is-leaving', 'is-playing')
      this.overlay.setAttribute('aria-hidden', 'true')
      document.body.classList.remove('has-lightbox-open')
      this.opened = false
      this.isLeaving = false

      if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
        this.lastFocus.focus()
      }
    }

    const onBackdropTransitionEnd = (e: TransitionEvent) => {
      if (e.target !== backdrop || e.propertyName !== 'opacity') return
      finalize()
    }

    if (backdrop) {
      backdrop.addEventListener('transitionend', onBackdropTransitionEnd)
    }

    const fallbackTimer = window.setTimeout(() => finalize(), 400)

    requestAnimationFrame(() => {
      this.overlay?.classList.add('is-leaving')
    })
  }

  private onCloseClick = () => this.close()

  private onKeydown = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && this.opened) this.close()
  }
}

export default new Lightbox()
